import React from "react";
import { Form, Select, Checkbox, Space, InputNumber, Radio, Divider } from "antd";
import { ILicenseTierOption, ISupportedFileFormatsResponse } from "../redux/types";
import { IPaddlePrice } from "Features/SubscriptionConfiguration/PaddleProducts/redux/types";
import type { LicenseSourceType } from "./index";

const { Option } = Select;

interface BasicConfigurationStepProps {
  packageNames: string[];
  tierOptions: ILicenseTierOption[];
  selectedRestrictions: string[];
  onRestrictionChange: (checkedValues: string[]) => void;
  licenseSource: LicenseSourceType;
  onLicenseSourceChange: (source: LicenseSourceType) => void;
  subscriptionPrices: IPaddlePrice[];
  productName: string;
  onSubscriptionPriceSelect: (priceId: string) => void;
  supportedFileFormats: ISupportedFileFormatsResponse | null;
}

const BasicConfigurationStep: React.FC<BasicConfigurationStepProps> = ({
  packageNames,
  tierOptions,
  selectedRestrictions,
  onRestrictionChange,
  licenseSource,
  onLicenseSourceChange,
  subscriptionPrices,
  productName,
  onSubscriptionPriceSelect,
  supportedFileFormats,
}) => {
  const formatPriceLabel = (price: IPaddlePrice) => {
    const priceName = price.customData?.price_name || price.description || price.priceId;
    const amount = (price.priceInCents / 100).toFixed(2);
    const interval = price.billingInterval === "annually" ? "yr" : "mo";
    const prefix = productName ? productName + " — " : "";
    return `${prefix}${priceName} — $${amount}/${interval}`;
  };

  return (
    <div>
      <Form.Item label="License Source">
        <Radio.Group
          value={licenseSource}
          onChange={(e) => onLicenseSourceChange(e.target.value)}
        >
          <Radio value="custom">Custom License</Radio>
          <Radio value="subscription">From Existing Subscription</Radio>
        </Radio.Group>
      </Form.Item>

      {licenseSource === "subscription" && (
        <Form.Item
          label="Subscription Plan"
          name="subscriptionPrice"
          rules={[{ required: true, message: "Please select a subscription plan" }]}
        >
          <Select
            placeholder="Select a subscription plan to prefill"
            onChange={(value: string) => onSubscriptionPriceSelect(value)}
            showSearch
            optionFilterProp="children"
          >
            {subscriptionPrices.map((price) => (
              <Option key={price.priceId} value={price.priceId}>
                {formatPriceLabel(price)}
              </Option>
            ))}
          </Select>
        </Form.Item>
      )}

      <Form.Item
        label="Package Name"
        name="packageName"
        rules={[{ required: true, message: "Please select a package name" }]}
      >
        <Select placeholder="Select package name">
          {packageNames.map((name) => (
            <Option key={name} value={name}>
              {name}
            </Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item label="Tier Selection" name="tierSelection">
        <Select placeholder="Select tier (optional)" allowClear>
          {tierOptions.map((tier) => (
            <Option key={tier.value} value={tier.name}>
              {tier.name}
            </Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item label="Restriction Options" name="restrictions">
        <Checkbox.Group value={selectedRestrictions} onChange={onRestrictionChange}>
          <Space direction="vertical">
            <Checkbox value="maxUsers">Max Users</Checkbox>
            <Checkbox value="maxAiTokens">Max AI Tokens</Checkbox>
            <Checkbox value="maxScheduledJobs">Max Scheduled Jobs</Checkbox>
            <Checkbox value="maxRowsAllowed">Max Rows Allowed</Checkbox>
            <Checkbox value="maxPagesAllowed">Max Pages Allowed</Checkbox>
            <Checkbox value="maxValidationRulesAllowed">Max Validation Rules Allowed</Checkbox>
            <Checkbox value="maxApiConnectorsAllowed">Max Api Connectors Allowed</Checkbox>
            <Checkbox value="maxAutomationWorkflowsAllowed">Max Automation Workflows Allowed</Checkbox>
            <Checkbox value="maxEmailIntegrationsAllowed">Max Email Integrations Allowed</Checkbox>
            <Checkbox value="maxNotificationRecipientsAllowed">Max Notification Recipients Allowed</Checkbox>
            <Checkbox value="supportedInputFormats">Supported Input Formats (Upload)</Checkbox>
            <Checkbox value="supportedOutputFormats">Supported Output Formats (Export/Download)</Checkbox>
          </Space>
        </Checkbox.Group>
      </Form.Item>

      {selectedRestrictions.includes("maxUsers") && (
        <Form.Item
          label="Max Users"
          name="maxUsers"
          rules={[{ type: "number", min: 1 }]}
        >
          <InputNumber min={1} style={{ width: "100%" }} />
        </Form.Item>
      )}

      {selectedRestrictions.includes("maxAiTokens") && (
        <Form.Item
          label="Max AI Tokens"
          name="maxAiTokens"
          rules={[{ type: "number", min: 0 }]}
        >
          <InputNumber min={0} style={{ width: "100%" }} />
        </Form.Item>
      )}

      {selectedRestrictions.includes("maxScheduledJobs") && (
        <Form.Item
          label="Max Scheduled Jobs"
          name="maxScheduledJobs"
          rules={[{ type: "number", min: 1 }]}
        >
          <InputNumber min={1} style={{ width: "100%" }} />
        </Form.Item>
      )}

      {selectedRestrictions.includes("maxRowsAllowed") && (
        <Form.Item
          label="Max Rows Allowed"
          name="maxRowsAllowed"
          rules={[{ type: "number", min: 0 }]}
        >
          <InputNumber min={0} style={{ width: "100%" }} />
        </Form.Item>
      )}

      {selectedRestrictions.includes("maxPagesAllowed") && (
        <Form.Item
          label="Max Pages Allowed"
          name="maxPagesAllowed"
          rules={[{ type: "number", min: 0 }]}
        >
          <InputNumber min={0} style={{ width: "100%" }} />
        </Form.Item>
      )}

      {selectedRestrictions.includes("maxValidationRulesAllowed") && (
        <Form.Item
          label="Max Validation Rules Allowed"
          name="maxValidationRulesAllowed"
          rules={[{ type: "number", min: 0 }]}
        >
          <InputNumber min={0} style={{ width: "100%" }} />
        </Form.Item>
      )}

      {selectedRestrictions.includes("maxApiConnectorsAllowed") && (
        <Form.Item
          label="Max Api Connectors Allowed"
          name="maxApiConnectorsAllowed"
          rules={[{ type: "number", min: 0 }]}
        >
          <InputNumber min={0} style={{ width: "100%" }} />
        </Form.Item>
      )}

      {selectedRestrictions.includes("maxAutomationWorkflowsAllowed") && (
        <Form.Item
          label="Max Automation Workflows Allowed"
          name="maxAutomationWorkflowsAllowed"
          rules={[{ type: "number", min: 0 }]}
        >
          <InputNumber min={0} style={{ width: "100%" }} />
        </Form.Item>
      )}

      {selectedRestrictions.includes("maxEmailIntegrationsAllowed") && (
        <Form.Item
          label="Max Email Integrations Allowed"
          name="maxEmailIntegrationsAllowed"
          rules={[{ type: "number", min: 0 }]}
        >
          <InputNumber min={0} style={{ width: "100%" }} />
        </Form.Item>
      )}

      {selectedRestrictions.includes("maxNotificationRecipientsAllowed") && (
        <Form.Item
          label="Max Notification Recipients Allowed"
          name="maxNotificationRecipientsAllowed"
          rules={[{ type: "number", min: 0 }]}
        >
          <InputNumber min={0} style={{ width: "100%" }} />
        </Form.Item>
      )}

      <Divider />

      {supportedFileFormats && selectedRestrictions.includes("supportedInputFormats") && (
        <Form.Item
          label="Supported File Input Formats (Upload)"
          name="supportedInputFormats"
          tooltip="Leave empty to allow all formats"
        >
          <Checkbox.Group>
            <Space direction="vertical">
              {supportedFileFormats.inputFormats.map((fmt) => (
                <Checkbox key={fmt.value} value={fmt.value}>
                  {fmt.label}
                </Checkbox>
              ))}
            </Space>
          </Checkbox.Group>
        </Form.Item>
      )}

      {supportedFileFormats && selectedRestrictions.includes("supportedOutputFormats") && (
        <Form.Item
          label="Supported File Output Formats (Export/Download)"
          name="supportedOutputFormats"
          tooltip="Leave empty to allow all formats"
        >
          <Checkbox.Group>
            <Space direction="vertical">
              {supportedFileFormats.outputFormats.map((fmt) => (
                <Checkbox key={fmt.value} value={fmt.value}>
                  {fmt.label}
                </Checkbox>
              ))}
            </Space>
          </Checkbox.Group>
        </Form.Item>
      )}
    </div>
  );
};

export default BasicConfigurationStep;
